import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, ArrowLeft, RefreshCw } from "lucide-react";
import { useRequestOtp, useVerifyOtp } from "../hooks/useClientAuth";
import { useClientAuth } from "../ClientAuthContext";
import { useBookingDraft } from "../../booking/BookingDraftContext";
import { useTranslation } from "react-i18next";

interface Props {
    onClose: () => void;
    onBack: () => void;
    onVerified: () => void;
}

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60; // seconds

export default function ClientOtpView({ onClose, onBack, onVerified }: Props) {
    const { t } = useTranslation();
    const [code, setCode] = React.useState("");
    const [error, setError] = React.useState<string | null>(null);
    const [resendCooldown, setResendCooldown] = React.useState(0);
    const [otpRequested, setOtpRequested] = React.useState(false);

    const requestOtpMutation = useRequestOtp();
    const verifyOtpMutation = useVerifyOtp();
    const { state, actions } = useClientAuth();
    const { draft } = useBookingDraft();
    const companyId = draft.companyId ?? 0;
    const phone = state.auth?.phone ?? "";

    // Auto-request OTP on mount
    React.useEffect(() => {
        if (!otpRequested && phone && companyId) {
            handleRequestOtp();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phone, companyId, otpRequested]);

    // Countdown timer for resend
    React.useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = setInterval(() => {
            setResendCooldown((prev) => Math.max(0, prev - 1));
        }, 1000);
        return () => clearInterval(timer);
    }, [resendCooldown]);

    const handleRequestOtp = async () => {
        setError(null);
        try {
            await requestOtpMutation.mutateAsync({
                phone,
                company_id: companyId,
            });
            setOtpRequested(true);
            setResendCooldown(RESEND_COOLDOWN);
        } catch (e: any) {
            const detail = e?.response?.data?.detail;
            const status = e?.response?.status;

            // If phone is already verified, mark as verified and proceed to dashboard
            if (status === 400 && detail === "Phone already verified") {
                actions.setVerified(true);
                onVerified();
                return;
            }

            if (status === 429) {
                setError(t("widget.client.otp.too_many_requests", "Too many requests. Please try again later."));
            } else if (status === 400 && detail === "Invalid or incomplete phone number") {
                // Backend phone gate: the number cannot receive SMS at all —
                // localize instead of echoing the EN backend string.
                setError(t("widget.common.validation.phone_invalid", "This phone number looks incomplete — please check it"));
            } else {
                setError(detail || e?.message || t("widget.client.otp.send_error", "Could not send the code"));
            }
        }
    };

    const handleVerify = async () => {
        if (code.length !== OTP_LENGTH) return;
        setError(null);
        try {
            const result = await verifyOtpMutation.mutateAsync({
                phone,
                code,
            });
            if (result.verified) {
                // Mark as verified and navigate to dashboard
                actions.setVerified(true);
                onVerified();
            }
        } catch (e: any) {
            const detail = e?.response?.data?.detail;
            setError(detail || t("widget.client.otp.invalid", "Invalid or expired code"));
        }
    };

    const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH);
        setCode(value);
    };

    const canResend = resendCooldown === 0 && !requestOtpMutation.isPending;

    return (
        <div className="bg-background h-[100dvh] min-h-[100dvh]">
            <div className="w-full h-full mx-auto sm:max-w-[420px] md:max-w-[520px]">
                <Card className="bg-card ios-shadow border-0 h-full flex flex-col sm:rounded-2xl sm:overflow-hidden">
                    {/* Header */}
                    <div className="p-4 border-b border-border/10 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Button variant="ghost" size="icon" onClick={onBack}>
                                <ArrowLeft className="w-5 h-5" />
                            </Button>
                            <h2 className="text-lg font-semibold">{t("widget.client.otp.title", "Phone verification")}</h2>
                        </div>
                        <Button variant="ghost" size="icon" onClick={onClose}>
                            <X className="w-5 h-5" />
                        </Button>
                    </div>

                    {/* Body */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        <div className="text-center space-y-2">
                            <p className="text-muted-foreground">
                                {t("widget.client.otp.sent_to", "We sent an SMS code to")}
                            </p>
                            <p className="font-semibold text-lg">{phone}</p>
                        </div>

                        <div className="space-y-3">
                            <div className="space-y-1.5">
                                <Label className="text-xs text-muted-foreground">
                                    {t("widget.client.otp.enter_code", "Enter the 6-digit code")}
                                </Label>
                                <Input
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="______"
                                    value={code}
                                    onChange={handleCodeChange}
                                    maxLength={6}
                                    className="text-center text-2xl tracking-[0.5em] font-mono"
                                    autoFocus
                                />
                            </div>

                            {error && (
                                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                                    {error}
                                </div>
                            )}
                        </div>

                        {/* Resend section */}
                        <div className="text-center">
                            {resendCooldown > 0 ? (
                                <p className="text-sm text-muted-foreground">
                                    {t("widget.client.otp.resend_in", "Send code again in {{count}} sec", { count: resendCooldown })}
                                </p>
                            ) : (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleRequestOtp}
                                    disabled={!canResend}
                                    className="text-primary"
                                >
                                    <RefreshCw className={`w-4 h-4 mr-2 ${requestOtpMutation.isPending ? "animate-spin" : ""}`} />
                                    {t("widget.client.otp.resend", "Send code again")}
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Footer CTA */}
                    <div
                        className="sticky bottom-0 inset-x-0 z-20 border-t border-border/10 bg-card/95 backdrop-blur"
                        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 16px)" }}
                    >
                        <div className="p-4 pt-3">
                            <Button
                                className="w-full h-12 rounded-xl font-semibold"
                                onClick={handleVerify}
                                disabled={verifyOtpMutation.isPending || code.length !== OTP_LENGTH}
                            >
                                {verifyOtpMutation.isPending ? t("widget.client.otp.verifying", "Verifying...") : t("widget.client.otp.submit", "Confirm")}
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}
